import React from 'react';
import {
  ComposedChart, Area, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { formatCurrency } from '../../utils/formatters';
import ChartDataCallout, { ChartDataCalloutList } from '../ui/ChartDataCallout';

const LEGEND_ITEMS = [
  { key: 'price', label: 'District avg price', color: '#10b981', type: 'line' },
  { key: 'national', label: 'National avg price', color: '#6366f1', type: 'dashed' },
  { key: 'transactions', label: 'District sales (bars)', color: '#64748b', type: 'bar' },
  { key: 'asking', label: 'This asking price', color: '#f59e0b', type: 'dashed' },
];

function ChartLegend({ showNational, showAsking }) {
  const items = LEGEND_ITEMS.filter((item) => {
    if (item.key === 'national') return showNational;
    if (item.key === 'asking') return showAsking;
    return true;
  });

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 pt-3 text-xs text-slate-500">
      {items.map((item) => (
        <span key={item.key} className="flex items-center gap-1.5">
          {item.type === 'bar' ? (
            <span
              className="w-3 h-3 rounded-sm inline-block border border-slate-600"
              style={{ backgroundColor: item.color, opacity: 0.9 }}
            />
          ) : item.type === 'dashed' ? (
            <span
              className="w-3 h-0 inline-block border-b border-dashed"
              style={{ borderColor: item.color }}
            />
          ) : (
            <span className="w-3 h-0.5 inline-block rounded" style={{ backgroundColor: item.color }} />
          )}
          {item.label}
        </span>
      ))}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-3 shadow-xl text-xs space-y-1">
      <p className="text-slate-400 mb-1.5 font-medium">{label}</p>
      {payload.map((p, i) => {
        if (p.dataKey === 'transactions') {
          return <p key={i} style={{ color: p.color }}>{p.value?.toLocaleString()} district sales</p>;
        }
        if (p.dataKey === 'national') {
          return <p key={i} style={{ color: p.color }}>National avg: {formatCurrency(p.value, true)}</p>;
        }
        return <p key={i} style={{ color: p.color }}>District avg: {formatCurrency(p.value, true)}</p>;
      })}
    </div>
  );
};

export default function SalesHistoryChart({ data, currentPrice, nationalData }) {
  if (!data?.length) {
    return (
      <div className="h-64 flex items-center justify-center text-slate-600">
        No sales history available
      </div>
    );
  }

  const nationalArray = Array.isArray(nationalData)
    ? nationalData
    : (Array.isArray(nationalData?.sales_by_year) ? nationalData.sales_by_year : []);
  const nationalByYear = {};
  nationalArray.forEach(n => { nationalByYear[n.year] = n.avg_price; });

  const chartData = data.map(d => ({
    year: d.year,
    price: Math.round(d.avg_price),
    transactions: d.transactions || 0,
    national: nationalByYear[d.year] ? Math.round(nationalByYear[d.year]) : undefined,
  }));

  const maxTransactions = Math.max(...chartData.map(d => d.transactions));
  const showNational = nationalArray.length > 0;

  return (
    <div>
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
          <XAxis dataKey="year" stroke="#475569" tick={{ fill: '#64748b', fontSize: 11 }} />
          <YAxis
            yAxisId="price"
            stroke="#475569"
            tick={{ fill: '#64748b', fontSize: 11 }}
            tickFormatter={v => `£${(v / 1000).toFixed(0)}K`}
            width={60}
            label={{
              value: 'Avg price',
              angle: -90,
              position: 'insideLeft',
              fill: '#64748b',
              fontSize: 10,
              offset: 10,
            }}
          />
          <YAxis
            yAxisId="vol"
            orientation="right"
            stroke="#475569"
            tick={{ fill: '#64748b', fontSize: 10 }}
            tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v}
            width={42}
            domain={[0, maxTransactions * 2.5]}
            label={{
              value: 'District sales',
              angle: 90,
              position: 'insideRight',
              fill: '#64748b',
              fontSize: 10,
              offset: 10,
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          {currentPrice && (
            <ReferenceLine
              yAxisId="price"
              y={currentPrice}
              stroke="#f59e0b"
              strokeDasharray="4 4"
              label={{ value: 'Asking', fill: '#f59e0b', fontSize: 11 }}
            />
          )}
          <Bar
            yAxisId="vol"
            dataKey="transactions"
            fill="#64748b"
            opacity={0.75}
            radius={[2, 2, 0, 0]}
            name="District sales (bars)"
          />
          <Area
            yAxisId="price"
            type="monotone"
            dataKey="price"
            stroke="#10b981"
            strokeWidth={2.5}
            fill="url(#priceGrad)"
            dot={{ fill: '#10b981', r: 3 }}
            activeDot={{ r: 5 }}
            name="District avg"
          />
          {showNational && (
            <Line
              yAxisId="price"
              type="monotone"
              dataKey="national"
              stroke="#6366f1"
              strokeWidth={1.5}
              strokeDasharray="5 3"
              dot={false}
              name="National avg"
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
      <ChartLegend showNational={showNational} showAsking={Boolean(currentPrice)} />
      <ChartDataCallout tone="info" title="How to read this chart" className="mt-4">
        <ChartDataCalloutList
          items={[
            'Green line — average sold price in this postcode district each year.',
            showNational ? 'Indigo dashed line — England & Wales average for comparison.' : null,
            'Grey bars — number of Land Registry sales in the district that year (right axis).',
            currentPrice ? 'Amber dashed line — this property\'s asking price on the price scale.' : null,
            'Bars are district-only. National sales volume is not overlaid — the scale would dwarf local data.',
          ].filter(Boolean)}
        />
      </ChartDataCallout>
    </div>
  );
}
